import React from 'react';
import { Button, Box } from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
  ProductInformationProps,
  StyledTextField,
} from '../productInformation';
import { ComplianceField } from 'src/Lca/compliance';

type SupplierEntry = {
  name: string;
  address: string;
  supplied_item: string;
  contact_person: string;
};

const emptySupplier: SupplierEntry = {
  name: '',
  address: '',
  supplied_item: '',
  contact_person: '',
};

const parseSuppliers = (rawValue: string): SupplierEntry[] => {
  if (!rawValue) {
    return [emptySupplier];
  }
  try {
    const parsed = JSON.parse(rawValue);
    if (!Array.isArray(parsed)) {
      return [emptySupplier];
    }
    return parsed.map((entry) => ({
      name: entry?.name || '',
      address: entry?.address || '',
      supplied_item: entry?.supplied_item || '',
      contact_person: entry?.contact_person || '',
    }));
  } catch {
    return [emptySupplier];
  }
};

const serializeSuppliers = (suppliers: SupplierEntry[]) => {
  const hasAnyFilled = suppliers.some(
    (entry) =>
      entry.name || entry.address || entry.supplied_item || entry.contact_person
  );

  // Keep empty rows when user adds multiple suppliers, so Add next supplier works.
  if (!hasAnyFilled && suppliers.length === 1) {
    return '';
  }
  return JSON.stringify(suppliers);
};

export default function Suppliers({
  setValues,
  values,
}: ProductInformationProps) {
  const { t } = useTranslation('lca', { keyPrefix: 'suppliers' });
  const suppliers = parseSuppliers(values.suppliers || '');

  const setSuppliers = (nextSuppliers: SupplierEntry[]) => {
    setValues({
      ...values,
      suppliers: serializeSuppliers(nextSuppliers),
    });
  };

  const updateSupplier = (
    index: number,
    key: keyof SupplierEntry,
    value: string
  ) => {
    const nextSuppliers = suppliers.map((supplier, i) =>
      i === index ? { ...supplier, [key]: value } : supplier
    );
    setSuppliers(nextSuppliers);
  };

  const addSupplier = () => setSuppliers([...suppliers, emptySupplier]);

  const removeSupplier = (index: number) => {
    const nextSuppliers = suppliers.filter((_, i) => i !== index);
    setSuppliers(nextSuppliers.length ? nextSuppliers : [emptySupplier]);
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
      {suppliers.map((supplier, index) => (
        <Box
          key={index}
          sx={{
            border: '1px solid #ececec',
            borderRadius: '12px',
            p: '16px',
            display: 'flex',
            flexDirection: 'column',
            gap: '16px',
          }}
        >
          <ComplianceField
            excelRow={33}
            regulatoryFrameworks={values.regulatory_frameworks}
          >
            <StyledTextField
              fullWidth
              label={t('keySupplier')}
              value={supplier.name}
              onChange={(e) => updateSupplier(index, 'name', e.target.value)}
            />
          </ComplianceField>
          <ComplianceField
            excelRow={34}
            regulatoryFrameworks={values.regulatory_frameworks}
          >
            <StyledTextField
              fullWidth
              label={t('supplierAddress')}
              value={supplier.address}
              onChange={(e) => updateSupplier(index, 'address', e.target.value)}
              multiline
              rows={2}
            />
          </ComplianceField>
          <ComplianceField
            excelRow={35}
            regulatoryFrameworks={values.regulatory_frameworks}
          >
            <StyledTextField
              fullWidth
              label={t('suppliedItem')}
              value={supplier.supplied_item}
              onChange={(e) =>
                updateSupplier(index, 'supplied_item', e.target.value)
              }
            />
          </ComplianceField>
          <ComplianceField
            excelRow={36}
            regulatoryFrameworks={values.regulatory_frameworks}
          >
            <StyledTextField
              fullWidth
              label={t('contactPerson')}
              value={supplier.contact_person}
              onChange={(e) =>
                updateSupplier(index, 'contact_person', e.target.value)
              }
            />
          </ComplianceField>
          <Box>
            <Button
              variant="secondary"
              size="small"
              onClick={() => removeSupplier(index)}
              disabled={suppliers.length === 1}
            >
              {t('removeSupplier')}
            </Button>
          </Box>
        </Box>
      ))}

      <Box>
        <Button variant="secondary" size="small" onClick={addSupplier}>
          {t('addSupplier')}
        </Button>
      </Box>
    </Box>
  );
}
